import { Injectable, inject } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { DevicesPayload } from '../../devices/components/devices.models';
import { HistoryApiService } from './history-api.service';
import { History, HistoryResponse, HistoryUi, UpdateType } from './history-api.types';
import {
  AlarmStatus,
  AttributeUpdatedValue,
  DeviceAttribute,
  HistoryTableData,
  HistoryUpdate,
  HistoryUpdateData,
  HistoryValues
} from './history-data.types';
import { isObject } from '../../../../shared/utils';
import { TimezoneService } from 'src/app/services/utility/timezone.service';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { TranslateService } from 'src/app/services/translate/translate.service';
import { roundedDecimalSymbols } from '@portal/shared/utils/math.utils';
import { notAvailable } from '@portal/shared/constants';

@Injectable({
  providedIn: 'root'
})
export class HistoryDataService {
  private historyApiService = inject(HistoryApiService);
  private timezoneService = inject(TimezoneService);

  private readonly updateType = new Map<UpdateType, string>([
    [UpdateType.AlarmStatusChanged, TranslateRegistry.get.FILTER.UPDATE_TYPE.ALARM_STATUS],
    [UpdateType.AssociatedDeviceUpdated, TranslateRegistry.get.FILTER.UPDATE_TYPE.ASSOCIATED_DEVICE_UPDATE],
    [UpdateType.BatteryStatusChanged, TranslateRegistry.get.FILTER.UPDATE_TYPE.BATTERY_STATUS],
    [UpdateType.BatteryVoltageStatusChanged, TranslateRegistry.get.FILTER.UPDATE_TYPE.BATTERY_VOLTAGE_STATUS],
    [UpdateType.ConfigurationUpdated, TranslateRegistry.get.FILTER.UPDATE_TYPE.CONFIGURATION],
    [UpdateType.DcuFirmwareUpdateRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.DCU_FIRMWARE_UPDATE_REQUEST],
    [UpdateType.DcuFirmwareUploadRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.DCU_FIRMWARE_UPLOAD_REQUEST],
    [UpdateType.DataRecoveryRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.DATA_RECOVERY_REQUEST],
    [UpdateType.DeviceInactivated, TranslateRegistry.get.FILTER.UPDATE_TYPE.DEVICE_INACTIVATED],
    [UpdateType.DeviceReinstalled, TranslateRegistry.get.FILTER.UPDATE_TYPE.DEVICE_REINSTALLED],
    [UpdateType.DeviceRemoved, TranslateRegistry.get.FILTER.UPDATE_TYPE.DEVICE_REMOVED],
    [UpdateType.FirmwareUpdated, TranslateRegistry.get.FILTER.UPDATE_TYPE.FIRMWARE_UPDATE],
    [UpdateType.HealthStatusChanged, TranslateRegistry.get.FILTER.UPDATE_TYPE.HEALTH_STATUS],
    [UpdateType.MtuFirmwareUpdateRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.MTU_FIRMWARE_UPDATE_REQUEST],
    [UpdateType.MtuFirmwareUploadRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.MTU_FIRMWARE_UPLOAD_REQUEST],
    [
      UpdateType.MtuTypeFirmwareUpdateRequest,
      TranslateRegistry.get.FILTER.UPDATE_TYPE.MTU_TYPE_FIRMWARE_UPDATE_REQUEST
    ],
    [UpdateType.NewDeviceInstalled, TranslateRegistry.get.FILTER.UPDATE_TYPE.NEW_DEVICE_INSTALLED],
    [UpdateType.OnDemandRead, TranslateRegistry.get.FILTER.UPDATE_TYPE.ON_DEMAND_READ],
    [UpdateType.ReadingReceived, TranslateRegistry.get.FILTER.UPDATE_TYPE.READING_RECEIVED],
    [UpdateType.TransmissionReadRateStatusChanged, TranslateRegistry.get.FILTER.UPDATE_TYPE.TRANSMISSION_READ_RATE],
    [
      UpdateType.ValidTransmissionReadRateStatusChanged,
      TranslateRegistry.get.FILTER.UPDATE_TYPE.VALID_TRANSMISSION_READ_RATE
    ],
    [UpdateType.ValveChangeRequest, TranslateRegistry.get.FILTER.UPDATE_TYPE.VALVE_CHANGE_REQUEST],
    [UpdateType.ValveStatus, TranslateRegistry.get.FILTER.UPDATE_TYPE.VALVE_STATUS],
    [UpdateType.TurnOffMtu, TranslateRegistry.get.FILTER.UPDATE_TYPE.TURN_OFF_MTU],
    [UpdateType.ReadMtu, TranslateRegistry.get.FILTER.UPDATE_TYPE.READ_MTU],
    [UpdateType.ReProgramming, TranslateRegistry.get.FILTER.UPDATE_TYPE.RE_PROGRAMMING],
    [UpdateType.NicConfigurationUpdated, TranslateRegistry.get.FILTER.UPDATE_TYPE.NIC_CONFIGURATION_UPDATED],
    [UpdateType.NicFirmwareUpdated, TranslateRegistry.get.FILTER.UPDATE_TYPE.NIC_FIRMWARE_UPDATED],
    [UpdateType.NicNewDeviceInstalled, TranslateRegistry.get.FILTER.UPDATE_TYPE.NIC_NEW_DEVICE_INSTALLED],
    [UpdateType.NicDeviceReinstalled, TranslateRegistry.get.FILTER.UPDATE_TYPE.NIC_DEVICE_REINSTALLED],
    [
      UpdateType.BaseStationRanConfigUpdateRequest,
      TranslateRegistry.get.FILTER.UPDATE_TYPE.BASE_STATION_RANCONFIG_UPDATE_REQUEST
    ]
  ]);

  private readonly initState: HistoryTableData = { data: [], total: 0 };

  protected readonly translate = inject(TranslateService);

  getTableData(tableState: DevicesPayload): Observable<HistoryTableData> {
    return this.historyApiService.getAll(tableState).pipe(
      map((history: HistoryResponse) => {
        if (!history) return this.initState;

        // TODO: Update HistoryUi naming: [iterateOverDifferentData()], [merged], and [attributes].
        // Current field and method names do not clearly reflect what they contain or do:
        // - [merged] is used for all non-attribute types (e.g., Events or Status updates).
        // - [attributes] is used for attribute types described in the [isAttributeType()] method.
        // Both fields exist in HistoryUi, and they cannot be populated or used simultaneously.
        // If an item's this.isAttributeType(item.updateType) === true, we use [attributes], else [merged].
        const { items, totalCount: total } = history;
        const data: HistoryUi[] = items
          ?.map((item: History) => {
            const newValues = item.newValues ? JSON.parse(item.newValues) : null;
            const oldValues = item.oldValues ? JSON.parse(item.oldValues) : null;
            return {
              ...item,
              dateTimeUtcSet: item.dateTimeUtcSet
                ? this.timezoneService.formatLong(item.dateTimeUtcSet)
                : item.dateTimeUtcSet,
              updateTypeName: this.translate.instant(this.updateType.get(item.updateType) ?? item.updateType),
              merged: {
                newValues: newValues ? this.iterateOverDifferentData(newValues, item.updateType) : null,
                oldValues: oldValues ? this.iterateOverDifferentData(oldValues, item.updateType) : null
              },
              attributes:
                this.isAttributeType(item.updateType) && oldValues && newValues
                  ? this.makeAttributeData(oldValues, newValues)
                  : null
            } as HistoryUi;
          })
          .map((item: HistoryUi) => {
            return {
              ...item,
              isExpandable: this.isAttributeType(item.updateType) && !this.#isSingleAttributeHistoryEntry(item),
              isExpanded:
                this.isAttributeType(item.updateType) && !this.#isSingleAttributeHistoryEntry(item) ? false : null
            } as HistoryUi;
          });

        return { data, total };
      }),
      catchError(err => {
        console.error(err);
        return of(this.initState);
      })
    );
  }

  private iterateOverDifferentData(obj: HistoryValues, updateType: UpdateType): HistoryUpdate {
    switch (updateType) {
      case UpdateType.HealthStatusChanged:
        if (obj.DeviceStatus?.DeviceStatusValue) {
          return { tag: obj.DeviceStatus?.DeviceStatusValue, value: null };
        } else {
          return null;
        }

      case UpdateType.TransmissionReadRateStatusChanged:
      case UpdateType.ValidTransmissionReadRateStatusChanged:
      case UpdateType.BatteryStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value } = obj.StatusComponents![0];
          return {
            tag: Severity,
            value: `${roundedDecimalSymbols(Value)}%`
          };
        } else {
          return null;
        }
      case UpdateType.BatteryVoltageStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value } = obj.StatusComponents![0];
          return {
            tag: Severity,
            value: `${roundedDecimalSymbols(Value)}V`
          };
        } else {
          return null;
        }
      case UpdateType.AlarmStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value, Name } = obj.StatusComponents![0];

          return {
            tag: Severity,
            value: Name,
            status: AlarmStatus[Value]
          };
        } else {
          return null;
        }
      case UpdateType.FirmwareUpdated:
        return obj.CommDeviceSoftware ? { tag: 'CommDeviceSoftware', value: obj.CommDeviceSoftware } : null;
      case UpdateType.NicFirmwareUpdated: {
        const nicAttr = obj.DeviceAttributes?.[0];
        return nicAttr?.Value ? { tag: nicAttr.Name, value: nicAttr.Value } : null;
      }

      case UpdateType.NicDeviceReinstalled:
      case UpdateType.DeviceReinstalled: {
        if (obj.Status) {
          return { value: obj.Status };
        } else {
          return null;
        }
      }
      case UpdateType.DeviceInactivated:
        if (obj.Status) {
          return { value: null, status: obj.Status };
        } else {
          return null;
        }

      case UpdateType.OnDemandRead:
      case UpdateType.ReadingReceived: //todo: ReadingReceived returns healthDetails array instead of the DeviceAttributes one, is this intended and we must update the code?
      case UpdateType.ValveChangeRequest:
      case UpdateType.DataRecoveryRequest:
      case UpdateType.MtuFirmwareUpdateRequest:
      case UpdateType.MtuFirmwareUploadRequest:
      case UpdateType.MtuTypeFirmwareUpdateRequest:
      case UpdateType.DcuFirmwareUpdateRequest:
      case UpdateType.DcuFirmwareUploadRequest:
      case UpdateType.BaseStationRanConfigUpdateRequest:
        return obj.DeviceAttributes?.[0].Value
          ? {
              tag: obj.DeviceAttributes![0].Name,
              value: obj.DeviceAttributes![0].Value,
              status: obj.DeviceAttributes![0].Status
            }
          : null;
      case UpdateType.ValveStatus: {
        const valve = obj.DeviceAttributes?.find(attribute => attribute.Name === 'ValvePosition');
        return valve
          ? {
              tag: valve.Name,
              value: valve.Value,
              status: valve.Status
            }
          : null;
      }
      default:
        return null;
    }
  }

  private hasValue(obj: HistoryValues): boolean {
    return !!obj?.StatusComponents?.length;
  }

  private makeAttributeData(oldValues: HistoryUpdateData, newValues: HistoryUpdateData): AttributeUpdatedValue[] {
    const nestedDisplayNameMap: Record<string, string> = {
      Comments: 'GeoLocationComment'
    };
    if (isObject(oldValues) && isObject(newValues)) {
      const nestedKeys: (keyof HistoryUpdateData)[] = [
        'DeviceSpecInfo',
        'GeoLocation',
        'DeviceAttributes',
        'CommDeviceInfo'
      ];

      const matchedNestedKeys = nestedKeys.filter(key => key in oldValues && key in newValues);
      if (matchedNestedKeys.length) {
        return matchedNestedKeys.reduce((acc: AttributeUpdatedValue[], nestedKey) => {
          const oldNested = oldValues[nestedKey] as Record<string, string>;
          const newNested = newValues[nestedKey] as Record<string, string>;

          return [
            ...acc,
            ...this.#extractValueFromAttribute(oldValues, newValues, nestedKey as string).map(
              (value: DeviceAttribute | string) => {
                if (this.#isConfigurationAttribute(value)) {
                  return this.#createAttributeUpdatedValue(
                    (oldNested as unknown as DeviceAttribute[])?.find(attr => attr.Name === value.Name),
                    (newNested as unknown as DeviceAttribute[])?.find(attr => attr.Name === value.Name)
                  );
                }

                return {
                  name: nestedDisplayNameMap[value] ?? value,
                  oldValue: oldNested[value] ? oldNested[value] : notAvailable,
                  newValue: newNested[value] ? newNested[value] : notAvailable
                };
              }
            )
          ];
        }, []);
      }
    }

    return Object.keys(oldValues).map(name => ({
      name,
      oldValue: this.#isDateValue(name)
        ? this.timezoneService.formatLong(this.#addUtcTimezoneIfAbsent(oldValues[name] as string))
        : (oldValues[name] as string | number),
      newValue: this.#isDateValue(name)
        ? this.timezoneService.formatLong(this.#addUtcTimezoneIfAbsent(newValues[name] as string))
        : (newValues[name] as string | number)
    }));
  }

  private isAttributeType(type: UpdateType): boolean {
    return (
      type === UpdateType.AssociatedDeviceUpdated ||
      type === UpdateType.ConfigurationUpdated ||
      type === UpdateType.NicConfigurationUpdated ||
      type === UpdateType.FirmwareUpdated
    );
  }

  #isDateValue(historyAttributeName: string): boolean {
    const dateAttributesLookup = new Set(['InstallationDateTimeUtc']);
    return dateAttributesLookup.has(historyAttributeName);
  }

  #addUtcTimezoneIfAbsent(time: string): string {
    const utcTz = '+00:00';
    return `${time}${time.includes(utcTz) ? '' : utcTz}`;
  }

  #isConfigurationAttribute(attr: string | DeviceAttribute): attr is DeviceAttribute {
    return !!(attr as DeviceAttribute)?.AttributeType;
  }

  #createAttributeUpdatedValue(oldValue?: DeviceAttribute, newValue?: DeviceAttribute): AttributeUpdatedValue {
    return {
      name: oldValue?.Name ?? newValue?.Name ?? notAvailable,
      oldValue: oldValue?.Value ?? notAvailable,
      newValue: newValue?.Value ?? notAvailable
    };
  }

  #isSingleAttributeHistoryEntry(historyEntry: HistoryUi): boolean {
    const attributes = historyEntry.attributes;
    return !!attributes && Array.isArray(attributes) && attributes.length === 1;
  }

  prepareValuesForExport(obj: HistoryValues, updateType: UpdateType): string {
    switch (updateType) {
      case UpdateType.HealthStatusChanged:
        if (obj.DeviceStatus?.DeviceStatusValue) {
          return `${obj.DeviceStatus?.DeviceStatusValue}`;
        } else {
          return notAvailable;
        }

      case UpdateType.TransmissionReadRateStatusChanged:
      case UpdateType.ValidTransmissionReadRateStatusChanged:
      case UpdateType.BatteryStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value } = obj.StatusComponents![0];
          return `${Severity} (${roundedDecimalSymbols(Value)}%)`;
        } else {
          return notAvailable;
        }
      case UpdateType.BatteryVoltageStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value } = obj.StatusComponents![0];
          return `${Severity} (${roundedDecimalSymbols(Value)}V)`;
        } else {
          return notAvailable;
        }
      case UpdateType.AlarmStatusChanged:
        if (this.hasValue(obj)) {
          const { Severity, Value, Name } = obj.StatusComponents![0];
          return Value === 0 ? AlarmStatus[0] : `${Name} (${Severity})`;
        } else {
          return notAvailable;
        }
      case UpdateType.FirmwareUpdated: {
        const preparedAttributeData = this.makeAttributeData(obj, obj)
          .map(item => `${item.name} (${item.oldValue ?? notAvailable})`)
          .join('; ');
        const commDeviceData = obj.CommDeviceSoftware ? `CommDeviceSoftware (${obj.CommDeviceSoftware})` : '';
        const combined = [preparedAttributeData, commDeviceData].filter(Boolean).join('; ');
        return obj ? combined : notAvailable;
      }
      case UpdateType.NicFirmwareUpdated: {
        const nicAttr = obj.DeviceAttributes?.[0];
        return nicAttr?.Value ? `${nicAttr.Name} (${nicAttr.Value})` : notAvailable;
      }

      case UpdateType.DeviceInactivated:
      case UpdateType.DeviceReinstalled:
      case UpdateType.NicDeviceReinstalled:
        if (obj.Status) {
          return `${obj.Status}`;
        } else {
          return notAvailable;
        }

      case UpdateType.OnDemandRead:
      case UpdateType.ReadingReceived:
      case UpdateType.ValveChangeRequest:
      case UpdateType.DataRecoveryRequest:
      case UpdateType.MtuFirmwareUpdateRequest:
      case UpdateType.MtuFirmwareUploadRequest:
      case UpdateType.MtuTypeFirmwareUpdateRequest:
      case UpdateType.DcuFirmwareUpdateRequest:
      case UpdateType.DcuFirmwareUploadRequest:
      case UpdateType.BaseStationRanConfigUpdateRequest:
        return obj.DeviceAttributes?.[0].Value ? `${obj.DeviceAttributes![0].Value}` : notAvailable;
      case UpdateType.ValveStatus: {
        const valve = obj.DeviceAttributes?.find(attribute => attribute.Name === 'ValvePosition');
        return valve ? `${valve.Value}` : notAvailable;
      }
      case UpdateType.AssociatedDeviceUpdated:
      case UpdateType.ConfigurationUpdated:
      case UpdateType.NicConfigurationUpdated: {
        const preparedAttributeData = this.makeAttributeData(obj, obj)
          .map(item => `${item.name} (${item.oldValue ?? notAvailable})`)
          .join('; ');
        return (obj && preparedAttributeData) || notAvailable;
      }
      default:
        return notAvailable;
    }
  }

  #extractValueFromAttribute(
    oldValues: HistoryUpdateData,
    newValues: HistoryUpdateData,
    key: string
  ): Array<DeviceAttribute | string> {
    const oldValue = oldValues[key];
    const newValue = newValues[key];

    if (Array.isArray(oldValue) || Array.isArray(newValue)) {
      const named = [
        ...((oldValue as DeviceAttribute[] | null) ?? []),
        ...((newValue as DeviceAttribute[] | null) ?? [])
      ].filter((value: DeviceAttribute) => value && typeof value === 'object' && !!value.Name);
      // Dedupe by Name: keeps the first position, but the latest (new) entry wins,
      // so a null AttributeType on the old side does not hide the attribute.
      return [...new Map(named.map(attr => [attr.Name, attr])).values()];
    }

    return [
      ...new Set([
        ...Object.keys((oldValue as Record<string, string> | null) ?? {}),
        ...Object.keys((newValue as Record<string, string> | null) ?? {})
      ])
    ];
  }
}
